import React, { useEffect, useRef, useState } from 'react';
import { Pagination as PaginationType } from 'shared/types';
import LeftChevronSVG from './svg/LeftChevronSVG';
import RightChevronSVG from './svg/RightChevronSVG';
import { Localizer } from 'services/Localizer';
import classnames from 'classnames';
import { createGuid } from './hawksearch/helpers/Cookies';

interface PaginationProps {
	pagination: PaginationType;
	goToNextPage: () => void;
	goToPreviousPage: () => void;
	goToPage: (page: number) => void;
	flush?: boolean;
}

export default function Pagination(props: PaginationProps) {
	const {
		flush,
		// #region HCL
		pagination: { totalPages, pageNumber },
		// #endregion
		goToNextPage,
		goToPage,
		goToPreviousPage,
	} = props;

	const [uniqueId] = useState(`pagination-input-${createGuid()}`);

	const [localPageNumber, setLocalPageNumber] = useState<string | undefined>(undefined);

	const timerRef = useRef<NodeJS.Timeout | null>(null);

	// make sure that the local page number state override is cleared out whenever the pagination page number
	// changes. this happens whenever a server request is completed and the lifted pagination state is updated
	useEffect(() => {
		setLocalPageNumber(undefined);
	}, [pageNumber]);

	function attemptGoToPage(newPageNumber: number) {
		if (newPageNumber < 1 || newPageNumber > totalPages) {
			// an invalid page number was entered, so clear out the user entry and do nothing.
			setLocalPageNumber(undefined);
		} else {
			goToPage(newPageNumber);
		}
	}

	// debounce the pager updates
	useEffect(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}

		timerRef.current = setTimeout(() => {
			if (localPageNumber) {
				attemptGoToPage(parseInt(localPageNumber));
			}
		}, 500);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [localPageNumber]);

	return (
		<div
			className={classnames('roc-pagination', {
				'roc-pagination--flush': flush,
			})}
		>
			<button
				type="button"
				className="roc-pagination__prev-btn"
				onClick={goToPreviousPage}
				// #region HCL
				disabled={pageNumber <= 1}
				// #endregion
				data-testid="pagination-left-btn"
				aria-label={Localizer('Previous')}
			>
				<LeftChevronSVG className="roc-pagination__icon" />
			</button>

			<label htmlFor={uniqueId}>
				<input
					type="number"
					className="roc-pagination__input roc-form-input__input"
					data-testid="pagination-page-number-input"
					value={localPageNumber !== undefined ? localPageNumber : pageNumber}
					onChange={(event) => {
						setLocalPageNumber(event.currentTarget.value);
					}}
					onKeyDown={(event) => {
						if (event.key === 'Enter' && localPageNumber) {
							// clear any pending debounce timeouts
							if (timerRef.current) {
								clearTimeout(timerRef.current);
							}

							attemptGoToPage(parseInt(localPageNumber));
						}
					}}
					id={uniqueId}
				/>
			</label>

			<span className="roc-pagination__total-text">{Localizer('OfTotalPages', totalPages.toLocaleString())}</span>

			<button
				type="button"
				className="roc-pagination__next-btn"
				onClick={goToNextPage}
				// #region HCL
				disabled={pageNumber >= totalPages}
				// #endregion
				data-testid="pagination-right-btn"
				aria-label={Localizer('Next')}
			>
				<RightChevronSVG className="roc-pagination__icon" />
			</button>
		</div>
	);
}
